
import { Component, Prop, Watch } from 'vue-property-decorator';
import { ActivityResponse } from '@/interfaces/Activities';
import { SupplierResponse } from '@/interfaces/Suppliers';
import { mapState, mapGetters } from 'vuex';
import { find, sortBy } from 'lodash';
import { ArrayNumericRange, is_number } from '@/config/functions';

import BasePage from '@/components/BasePage';
import { ActivitiesApi } from '@/api/ActivitiesApi';
import md5 from 'md5';

@Component({
   computed: {
      ...mapState({
         suppliers: (s: any) => s.suppliers.suppliers,
         tarification: (s: any) => s.activities.tarification,
         time_of_year: (s: any) => s.activities.time_of_year,
         type_of_activity: (s: any) => s.activities.type_of_activity,
         type_of_transfer: (s: any) => s.activities.type_of_transfer,
         months: (s: any) => s.activities.months,
         weather: (s: any) => s.activities.weather,
         suitable_for_disable: (s: any) => s.activities.suitable_for_disable,
         activities_colors: (s: any) => sortBy(s.activities.colors, 'd_sort'),
      }),
      ...mapGetters({
         GP_THRESHOLD: 'activities/GP_THRESHOLD',
      }),
   },
   components: {
      ObjectFiles: () => import('@/components/ObjectFiles.vue'),
   },
})
export default class Form extends BasePage {
   @Prop({ type: [Object] }) model!: ActivityResponse;

   @Prop({ type: [Object] }) o_cost_new!: any;

   @Prop({ type: [Object] }) o_cost_current!: any;

   @Prop({ type: [Number, String], default: 0 }) id!: number | string;

   suppliers!: SupplierResponse[];

   activities_colors!: any[];

   weather!: any[];

   type_of_transfer!: any[];

   tarification!: any[];

   time_of_year!: any[];

   type_of_activity!: any[];

   months!: any[];

   suitable_for_disable!: any[];

   GP_THRESHOLD!: number;

   form: ActivityResponse | null = null;

   o_activity_cost_table: any = {
      fields: [],
      items: [],
   };

   o_activity_price_table: any = {
      fields: [],
      items: [],
   };

   o_transfer_cost_table: any = {
      fields: [],
      items: [],
   };

   o_gp_table: any = {
      fields: [],
      items: [],
   };

   o_task_list_fields: any[] = [
      {
         key: 'task_name',
         label: 'Task',
      },
      {
         key: 'days_left',
         label: 'Days Until Due',
         thAttr: {
            style: 'width: 150px;',
         },
      },
   ];

   createHash(form: any = {}): string {
      return md5(
         JSON.stringify({
            ...this.model,
            ...form,
            o_average_gp: null,
            o_minimum_gp: null,
            o_maximum_gp: null,
            o_new_average_gp: null,
            o_new_minimum_gp: null,
            o_new_maximum_gp: null,
            o_gp_table: null,
            o_transfer_cost_table: null,
            o_activity_cost_table: null,
            o_activity_price_table: null,
         })
      );
   }

   mounted() {
      this.$root.$on('header:save', this.submit);
      this.$root.$on('header:copy', this.duplicate);
      this.$root.$on('header:remove', this.archive);
      this.$root.$on('header:restore', this.unarchive);

      this.onChangeModel();
   }

   beforeDestroy() {
      this.$root.$off('header:save', this.submit);
      this.$root.$off('header:copy', this.duplicate);
      this.$root.$off('header:remove', this.archive);
      this.$root.$off('header:restore', this.unarchive);
   }

   submit() {
      if (!this.form) return;

      this.$store.commit('showLoading');
      ActivitiesApi.update(this.model.o_id, {
         ...this.form,
         o_gp_table: this.o_gp_table.items,
         o_transfer_cost_table: this.o_transfer_cost_table['items'],
         o_activity_cost_table: this.o_activity_cost_table['items'],
         o_activity_price_table: this.o_activity_price_table['items'],
         o_cost_from: this.formatterDate(this.form['o_cost_from']),
         o_price_from: this.formatterDate(this.form['o_price_from']),
         o_average_gp: this.parseFloatCustom(this.form['o_average_gp']),
         o_maximum_gp: this.parseFloatCustom(this.form['o_maximum_gp']),
         o_minimum_gp: this.parseFloatCustom(this.form['o_minimum_gp']),
         o_new_average_gp: this.parseFloatCustom(this.form['o_new_average_gp']),
         o_new_maximum_gp: this.parseFloatCustom(this.form['o_new_maximum_gp']),
         o_new_minimum_gp: this.parseFloatCustom(this.form['o_new_minimum_gp']),
      })
         .then(() => {
            this.$bvModal.msgBoxOk('Saved successfully');
            return this.$emit('get-order');
         })
         .finally(() => {
            this.$store.commit('hideLoading');
         });
   }

   duplicate() {
      if (!window.confirm('Do you want to create a copy of this object?')) {
         return;
      }

      this.$store.commit('showLoading');
      ActivitiesApi.duplicate(this.model.o_id)
         .then(({ id }: any) => {
            this.$router.push({ name: 'settings:activity.edit', params: { id } });
         })
         .finally(() => {
            this.$store.commit('hideLoading');
         });
   }

   archive() {
      this.$store.commit('showLoading');
      ActivitiesApi.archive(this.model.o_id)
         .then(() => this.$emit('get-order'))
         .finally(() => {
            this.$store.commit('hideLoading');
         });
   }

   unarchive() {
      this.$store.commit('showLoading');
      ActivitiesApi.unarchive(this.model.o_id)
         .then(() => this.$emit('get-order'))
         .finally(() => {
            this.$store.commit('hideLoading');
         });
   }

   @Watch('model', { deep: true })
   onChangeModel() {
      this.form = { ...this.model, o_traveltime: this.formatterTime(this.model.o_traveltime) };

      this.$store.commit('setItems', {
         name: 'beforeunload',
         value: this.createHash(),
      });

      this.$store.commit('setItems', {
         name: 'loadBeforeunload',
         value: this.createHash(),
      });
   }

   @Watch('form', { deep: true })
   onChangeForm(n: ActivityResponse) {
      const columns = ArrayNumericRange(6, 32);

      const fields = ['', ...columns].map((label, key) => ({
         key: key.toString(),
         label: label.toString(),
         formatter: (value: string | number) => (is_number(value) ? this.formatterFloat(value) : value),
      }));

      this.calculateActivityCostTable(columns, fields);
      this.calculateActivityPriceTable(columns, fields);
      this.calculateTransferCostTable(columns, fields);
      this.calculateGPTable(columns, fields);

      this.$store.commit('setItems', {
         name: 'beforeunload',
         value: this.createHash(n),
      });
   }

   calculateActivityPriceTable(columns: number[], fields: any[]) {
      if (this.form) {
         const o_per = find(this.tarification, { id: this.form['o_perr'] });
         const o_per_new = find(this.tarification, { id: this.form['o_perr_new'] });

         const o_lsp = this.parseFloatCustom(this.form['o_lsp'] ?? 0);
         const o_new_lsp = this.parseFloatCustom(this.form['o_new_lsp'] ?? 0);

         this.o_activity_price_table = {
            fields,
            items: [
               [
                  'Current',
                  ...columns.map(pax => {
                     if (o_per?.d_name == 'PP') {
                        return o_lsp;
                     } else if (o_per?.d_name == 'PG') {
                        return o_lsp / pax;
                     }
                     return 0;
                  }),
               ],
               [
                  'New',
                  ...columns.map(pax => {
                     if (o_per_new?.d_name == 'PP') {
                        return o_new_lsp;
                     } else if (o_per_new?.d_name == 'PG') {
                        return o_new_lsp / pax;
                     }
                     return 0;
                  }),
               ],
            ],
         };
      }
   }

   calculateActivityCostTable(columns: number[], fields: any[]) {
      if (this.form) {
         const o_per = find(this.tarification, { id: this.form['o_per'] });
         const o_per_new = find(this.tarification, { id: this.form['o_per_new'] });

         const o_cost = this.parseFloatCustom(this.form['o_cost'] ?? 0);
         const new_supp_cost = this.parseFloatCustom(this.form['o_new_supp_cost'] ?? o_cost);

         const additional_cost = this.parseFloatCustom(this.form['o_additional_cost'] ?? 0);
         const additional_cost_pp = this.parseFloatCustom(this.form['o_additional_cost_pp'] ?? 0);

         const o_new_additional_cost = this.parseFloatCustom(this.form['o_new_additional_cost'] ?? 0);
         const o_new_additional_cost_pp = this.parseFloatCustom(this.form['o_new_additional_cost_pp'] ?? 0);

         this.o_activity_cost_table = {
            fields,
            items: [
               [
                  'Current',
                  ...columns.map(pax => {
                     if (o_per?.d_name == 'PP') {
                        return o_cost + additional_cost / pax + additional_cost_pp;
                     } else if (o_per?.d_name == 'PG') {
                        return o_cost / pax + additional_cost / pax + additional_cost_pp;
                     }
                     return 0;
                  }),
               ],
               [
                  'New',
                  ...columns.map(pax => {
                     if (o_per_new?.d_name == 'PP') {
                        return new_supp_cost + o_new_additional_cost / pax + o_new_additional_cost_pp;
                     } else if (o_per_new?.d_name == 'PG') {
                        return new_supp_cost / pax + o_new_additional_cost / pax + o_new_additional_cost_pp;
                     }
                     return 0;
                  }),
               ],
            ],
         };
      }
   }

   calculateTransferCostTable(columns: number[], fields: any[]) {
      if (this.form) {
         const $tz = this.form.o_transfer_zone;
         const o_type_transfer = this.form.o_type_transfer;
         const o_transfer_waiting_time = this.form.o_transfer_waiting_time;

         const pax_table_name = (pax: number) => {
            if (pax <= 7) return 'o_cost_7pax';
            if (pax <= 16) return 'o_cost_8_16pax';
            if (pax <= 23) return 'o_cost_17_23pax';
            return 'o_cost_24_32pax';
         };

         this.o_transfer_cost_table = {
            fields,
            items: [
               [
                  'Current',
                  ...columns.map(pax => {
                     if ($tz && ![2100, 2099].includes(o_type_transfer)) {
                        const pax_table = this.o_cost_current[pax_table_name(pax)];
                        return pax_table[$tz - 1][o_transfer_waiting_time + 1] / pax;
                     }
                     return 0;
                  }),
               ],
               [
                  'New',
                  ...columns.map(pax => {
                     if ($tz && ![2100, 2099].includes(o_type_transfer)) {
                        const pax_table = this.o_cost_new[pax_table_name(pax)];
                        return pax_table[$tz - 1][o_transfer_waiting_time + 1] / pax;
                     }
                     return 0;
                  }),
               ],
            ],
         };
      }
   }

   calculateGPTable(columns: number[], fields: any[]) {
      if (this.form) {
         const $ac = this.o_activity_cost_table['items'];
         const $tc = this.o_transfer_cost_table['items'];

         const o_lsp = this.parseFloatCustom(this.form['o_lsp'] ?? 0);
         const o_perr = find(this.tarification, { id: this.form['o_perr'] });
         const o_per_new = find(this.tarification, { id: this.form['o_perr_new'] });

         const o_new_lsp = this.parseFloatCustom(this.form['o_new_lsp'] ?? 0);

         const currentGP: number[] = columns.map((pax, i) => {
            if (o_lsp <= 0) return 0;

            if (o_perr?.d_name == 'PP') {
               return o_lsp - $ac[0][i + 1] - $tc[0][i + 1];
            } else if (o_perr?.d_name == 'PG') {
               return o_lsp / pax - $ac[0][i + 1] - $tc[0][i + 1];
            }
            return 0;
         });

         const newGP: number[] = columns.map((pax, i) => {
            if (o_new_lsp <= 0) return 0;

            if (o_per_new?.d_name == 'PP') {
               return o_new_lsp - $ac[1][i + 1] - $tc[1][i + 1];
            } else if (o_per_new?.d_name == 'PG') {
               return o_new_lsp / pax - $ac[1][i + 1] - $tc[1][i + 1];
            }
            return 0;
         });

         this.o_gp_table = {
            fields,
            items: [
               ['Current', ...currentGP],
               ['New', ...newGP],
            ],
         };

         this.form['o_average_gp'] = this.formatterFloat((currentGP[2] + currentGP[3] + currentGP[4] + currentGP[6]) / 4);
         this.form['o_new_average_gp'] = this.formatterFloat((newGP[2] + newGP[3] + newGP[4] + newGP[6]) / 4);

         const min_col = Math.min(...columns);
         const max_col = Math.max(...columns);

         const o_listmingroup = this.form['o_listmingroup'] && this.form['o_listmingroup'] > 0 ? this.form['o_listmingroup'] : min_col;
         this.form['o_minimum_gp'] = this.formatterFloat(o_listmingroup > max_col ? Math.max(...currentGP) : currentGP[o_listmingroup - min_col]);
         this.form['o_maximum_gp'] = this.formatterFloat(Math.max(...currentGP));

         const o_new_minimum = this.form['o_new_minimum'] && this.form['o_new_minimum'] > 0 ? this.form['o_new_minimum'] : min_col;
         this.form['o_new_minimum_gp'] = this.formatterFloat(o_new_minimum > max_col ? Math.max(...newGP) : newGP[o_new_minimum - min_col]);
         this.form['o_new_maximum_gp'] = this.formatterFloat(Math.max(...newGP));
      }
   }
}
